/**
 * Settings "Danger Zone" — org self-management for an org owner/admin.
 *
 * Renders three destructive/sensitive actions, each server-gated (the API
 * re-checks org.transfer_ownership / org.delete and derives the org from the
 * session — the org id is NEVER sent from here):
 *   1. Transfer ownership — pick another active user in the org.
 *   2. Close organization — soft-delete (reversible by a platform admin).
 *   3. Delete organization — permanent; requires typing the org handle to confirm.
 *
 * INTEGRATION: mount <DangerZone orgSlug={settings.slug} /> at the bottom of the
 * Settings page (apps/web/src/routes/settings/index.tsx), passing the org slug
 * from the already-loaded org settings. It is self-contained (owns its own
 * queries/mutations). The UI shows it to everyone but the API enforces the gate;
 * a forbidden response surfaces inline.
 */
import { ApiError } from '@fc/core'
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeftRight, Ban, Trash2 } from 'lucide-react'
import { useState } from 'react'
import {
  closeOrg,
  deleteOrg,
  listOwnershipCandidates,
  transferOwnership,
} from '../../lib/admin-api'

const fieldClass =
  'flex h-10 w-full rounded-md border border-border bg-bg-alt px-3 py-2 text-sm text-fg ' +
  'focus-visible:outline-none focus-visible:border-secondary focus-visible:ring-[3px] focus-visible:ring-ring'

export function DangerZone({ orgSlug }: { orgSlug: string }) {
  return (
    <Card className="max-w-3xl border-danger/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-danger">
          <AlertTriangle className="size-4" />
          Danger Zone
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-8">
        <TransferOwnership />
        <div className="h-px bg-border" />
        <CloseOrg />
        <div className="h-px bg-border" />
        <DeleteOrg orgSlug={orgSlug} />
      </CardContent>
    </Card>
  )
}

// ----------------------------- transfer ownership -----------------------------

function TransferOwnership() {
  const [toUserId, setToUserId] = useState('')
  const [done, setDone] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['ownership-candidates'],
    queryFn: listOwnershipCandidates,
  })

  const mutation = useMutation({
    mutationFn: () => transferOwnership({ toUserId }),
    onSuccess: () => setDone(true),
  })

  return (
    <section className="space-y-3">
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <ArrowLeftRight className="size-4 text-secondary" />
          Transfer ownership
        </h3>
        <p className="text-xs text-muted">
          Make another active member an Org Owner. Your access is unchanged; the organization always
          keeps at least one owner.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted">
          <Spinner /> Loading members…
        </div>
      ) : isError ? (
        <p className="text-xs text-dim">You may not have permission to transfer ownership.</p>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1 space-y-1.5">
            <Label htmlFor="transfer-to">New owner</Label>
            <select
              id="transfer-to"
              className={fieldClass}
              value={toUserId}
              onChange={(e) => setToUserId(e.target.value)}
            >
              <option value="">Select a member…</option>
              {(data ?? []).map((c) => (
                <option key={c.userId} value={c.userId}>
                  {c.name} ({c.email})
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="secondary"
            disabled={!toUserId || mutation.isPending}
            onClick={() => {
              setDone(false)
              mutation.mutate()
            }}
          >
            {mutation.isPending ? <Spinner /> : null}
            Transfer
          </Button>
        </div>
      )}

      {done && (
        <p className="text-sm text-success" role="status">
          Ownership transferred.
        </p>
      )}
      {mutation.isError && (
        <p className="text-sm text-danger" role="alert">
          {mutation.error instanceof ApiError ? mutation.error.message : 'Could not transfer.'}
        </p>
      )}
    </section>
  )
}

// ----------------------------- close org -----------------------------

function CloseOrg() {
  const [confirming, setConfirming] = useState(false)
  const mutation = useMutation({
    mutationFn: () => closeOrg(),
    onSuccess: () => {
      // The org is suspended and all sessions revoked; bounce to login.
      window.location.assign('/login')
    },
  })

  return (
    <section className="space-y-3">
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Ban className="size-4 text-warning" />
          Close organization
        </h3>
        <p className="text-xs text-muted">
          Suspends the organization and signs everyone out. Data is retained and a platform
          administrator can reactivate it later.
        </p>
      </div>
      {confirming ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-warning">Close this organization for everyone?</span>
          <Button
            variant="secondary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? <Spinner /> : null}
            Yes, close it
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button variant="secondary" onClick={() => setConfirming(true)}>
          Close organization
        </Button>
      )}
      {mutation.isError && (
        <p className="text-sm text-danger" role="alert">
          {mutation.error instanceof ApiError ? mutation.error.message : 'Could not close the org.'}
        </p>
      )}
    </section>
  )
}

// ----------------------------- delete org -----------------------------

function DeleteOrg({ orgSlug }: { orgSlug: string }) {
  const [confirmSlug, setConfirmSlug] = useState('')
  const matches = confirmSlug.trim().toLowerCase() === orgSlug.toLowerCase()

  const mutation = useMutation({
    mutationFn: () => deleteOrg({ confirmSlug }),
    onSuccess: () => {
      // Org and all data are gone; the session is dead. Bounce to the landing.
      window.location.assign('/')
    },
  })

  return (
    <section className="space-y-3">
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-danger">
          <Trash2 className="size-4" />
          Delete organization permanently
        </h3>
        <p className="text-xs text-muted">
          This permanently deletes the organization and <strong>all</strong> of its data. This
          cannot be undone. Type the handle{' '}
          <code className="rounded bg-surface-2 px-1 py-0.5 text-fg">{orgSlug}</code> to confirm.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-56 flex-1 space-y-1.5">
          <Label htmlFor="delete-confirm">Organization handle</Label>
          <Input
            id="delete-confirm"
            value={confirmSlug}
            onChange={(e) => setConfirmSlug(e.target.value)}
            placeholder={orgSlug}
            autoComplete="off"
          />
        </div>
        <Button
          variant="danger"
          disabled={!matches || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? <Spinner /> : null}
          Delete forever
        </Button>
      </div>
      {mutation.isError && (
        <p className="text-sm text-danger" role="alert">
          {mutation.error instanceof ApiError
            ? mutation.error.message
            : 'Could not delete the org.'}
        </p>
      )}
    </section>
  )
}
